import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { VideoListProps } from './contracts';
import VideoListUI from './VideoListUI';
import { usePublishedVideosQuery, useInfinitePublishedVideosQuery } from '@data/videos/usePublishedVideosQuery';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader/SkeletonLoader';
import { Video, PaginatedResponse } from '@repo/types';

export function VideoList({
  tags,
  tagsMatchMode,
  excludeTags,
  videoChannels,
  limit = 6,
  lazyLoadPagination = false,
  sortOrder,
  ...props
}: VideoListProps) {
  const [searchParams] = useSearchParams();
  const offset =
    searchParams.get('offset') && typeof parseInt(searchParams.get('offset') as string) === 'number'
      ? parseInt(searchParams.get('offset') as string)
      : 1;

  const queryArgs = {
    tags: tags ?? undefined,
    tagsMatchMode: tagsMatchMode ?? undefined,
    excludeTags: excludeTags ?? undefined,
    videoChannelIds: videoChannels ?? undefined,
    limit: limit ?? undefined,
    sortOrder: sortOrder ?? undefined,
  };

  // Use regular query when lazyLoadPagination is disabled
  const regularQuery = usePublishedVideosQuery({ ...queryArgs, page: offset }, { enabled: !lazyLoadPagination });

  // Use infinite query when lazyLoadPagination is enabled
  const infiniteQuery = useInfinitePublishedVideosQuery(queryArgs, { enabled: lazyLoadPagination });

  const isLoading = lazyLoadPagination ? infiniteQuery.isLoading : regularQuery.isLoading;
  const error = lazyLoadPagination ? infiniteQuery.error : regularQuery.error;

  // Flatten all videos from infinite query pages
  const allVideos = useMemo(() => {
    if (!lazyLoadPagination || !infiniteQuery.data) return [];
    return infiniteQuery.data.pages.flatMap((page) => page.items);
  }, [lazyLoadPagination, infiniteQuery.data]);

  // Create a synthetic PaginatedResponse for the UI component
  const paginatedVideos: PaginatedResponse<Video> | undefined = useMemo(() => {
    if (lazyLoadPagination && infiniteQuery.data?.pages && infiniteQuery.data.pages.length > 0) {
      const lastPage = infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1];
      if (!lastPage) return regularQuery.data;
      return {
        items: allVideos,
        pagination: lastPage.pagination,
      };
    }
    return regularQuery.data;
  }, [lazyLoadPagination, infiniteQuery.data, allVideos, regularQuery.data]);

  if (isLoading) {
    return (
      <div style={{ display: 'block', width: '100%', height: '100%' }}>
        <SkeletonLoader count={limit * 5 - 1} />
      </div>
    );
  }

  if (!paginatedVideos || error) {
    return null;
  }

  return (
    <VideoListUI
      paginatedVideos={paginatedVideos}
      allVideos={allVideos}
      lazyLoadPagination={lazyLoadPagination}
      hasNextPage={infiniteQuery.hasNextPage}
      isFetchingNextPage={infiniteQuery.isFetchingNextPage}
      fetchNextPage={infiniteQuery.fetchNextPage}
      limit={limit}
      {...props}
    />
  );
}
